// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { Mesh, MeshStandardMaterial, Vector3 } from 'three';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { createHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/HTMLMesh';
import { InteractiveGroup } from '@/_models/virtualStaging/HTMLMesh/utils/InteractiveGroup';
import { getXZCoordinatesOnCircle } from '@/components/util/math/getXZCoordinatesOnCircle';
import { AbstractHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/component/AbstractHTMLMesh';
import { ControllerType, Inputs } from '@/_models/virtualStaging/HTMLMesh/component/Menu/types';

export class MenuMeshComponent extends AbstractHTMLMesh {
  private hostPose;

  public selectedItemIndex = 0;

  public mesh;

  public interactiveGroup;

  public gamepadButtonCallback = null;

  public thumbStickCallback = null;

  public triggerCallback = null;

  inputs: Inputs = {
    html: null,
    visible: false,
    scale: 1,
    itemRefArr: [],
    controller: ControllerType.GamepadL,
    controllerButtonIndex: 4,
    yOffset: -0.2,
  };

  onInit() {
    const { html } = this.inputs;
    this.initMesh(html);
    this.initRoot();
    this.addMeshPointerEvents();
    this.getXRController();
    this.outputs.loadingState = LoadingState.Loaded;
  }

  initMesh(html) {
    this.mesh = createHTMLMesh(html, this.context.three);
    this.mesh.material.side = this.context.three.DoubleSide;
    this.mesh.material.depthWrite = false;
    this.mesh.material.depthTest = false;
    this.mesh.renderOrder = 980;
    this.mesh.scale.setScalar(2);
    this.mesh.userData = { isPointerOver: false };
  }

  addMeshPointerEvents() {
    this.interactiveGroup = new InteractiveGroup(
      this.context.renderer,
      this.context.camera,
      this.context,
    );
    this.pivot.add(this.interactiveGroup);
    this.interactiveGroup.add(this.mesh);
  }

  setHostPose(pose) {
    this.hostPose = pose;
  }

  setPositionFromPose(pose) {
    if (!pose.rotation || !pose.position) {
      console.error('No pose', pose);
      console.trace(pose);
      return;
    }
    const { x, z } = getXZCoordinatesOnCircle(0.85, pose.rotation.y + 180);
    this.root.position.set(
      pose.position.x + x,
      pose.position.y + this.inputs.yOffset,
      pose.position.z + z,
    );
    this.root.lookAt(pose.position.x, pose.position.y - 0.1, pose.position.z);
  }

  setFromHostPose() {
    this.setPositionFromPose(this.hostPose);
  }

  setFromController(controller: any) {
    const controllerWorld = new Vector3();
    controller.getWorldPosition(controllerWorld);
    this.root.position.copy(controllerWorld);
  }

  updateMesh() {
    this.interactiveGroup.traverse((el: Mesh) => {
      if (el.material) {
        const material = el.material as MeshStandardMaterial;
        if (material.map) material.map.dispose();
        material.dispose();
      }
      if (el.geometry) {
        el.geometry.dispose();
      }
    });
    this.pivot.remove(this.interactiveGroup);
    this.initMesh(this.inputs.html);
    this.addMeshPointerEvents();
  }

  selectItem(index: number) {
    if (!this.inputs.itemRefArr[this.selectedItemIndex] || !this.inputs.itemRefArr[index]) return;
    this.inputs.itemRefArr[this.selectedItemIndex].style.backgroundColor = 'transparent';
    this.inputs.itemRefArr[index].style.backgroundColor = 'blue';
    this.selectedItemIndex = index;
  }

  selectItemUp() {
    this.selectItem(
      this.inputs.itemRefArr.length - 1 === this.selectedItemIndex ? 0 : this.selectedItemIndex + 1,
    );
  }

  selectItemDown() {
    this.selectItem(
      this.selectedItemIndex === 0 ? this.inputs.itemRefArr.length - 1 : this.selectedItemIndex - 1,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  cycleVR(_session) {
    if (this.hostPose && this.inputs.visible) this.setFromHostPose();

    if (this.inputs.controller) {
      if (this[this.inputs.controller]) {
        if (
          !this.gamepadThumbStickYActive &&
          (this[this.inputs.controller].axes[3] < -0.5 ||
            this[this.inputs.controller].axes[3] > 0.5)
        ) {
          this.gamepadThumbStickYActive = true;
          const directionY = this[this.inputs.controller].axes[3] > 0.5 ? 1 : -1;
          if (directionY === 1) this.selectItemUp();
          if (directionY === -1) this.selectItemDown();
          if (this.thumbStickCallback) this.thumbStickCallback(this[this.inputs.controller].axes);
        }
        if (
          this.gamepadThumbStickYActive &&
          this[this.inputs.controller].axes[3] >= -0.5 &&
          this[this.inputs.controller].axes[3] <= 0.5
        ) {
          this.gamepadThumbStickYActive = false;
        }
      }
      if (
        this[this.inputs.controller]?.buttons[this.inputs.controllerButtonIndex]?.pressed &&
        !this.gamepadButtonActive &&
        this.hostPose
      ) {
        this.setVisible(!this.inputs.visible);
        this.gamepadButtonActive = true;
        if (this.gamepadButtonCallback)
          this.gamepadButtonCallback(
            this[this.inputs.controller]?.buttons[this.inputs.controllerButtonIndex],
          );
      }
      if (
        !this[this.inputs.controller]?.buttons[this.inputs.controllerButtonIndex]?.pressed &&
        this.gamepadButtonActive
      ) {
        this.gamepadButtonActive = false;
      }

      if (
        this[this.inputs.controller]?.buttons[4]?.pressed &&
        !this.triggerActive &&
        this.hostPose
      ) {
        this.triggerActive = true;
        if (this.triggerCallback) this.triggerCallback(this[this.inputs.controller]?.buttons);
      }
      if (!this[this.inputs.controller]?.buttons[4]?.pressed && this.triggerActive) {
        this.triggerActive = false;
      }
    }
  }

  onTick() {
    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame(() => this.cycleVR(_session));
    }
  }
}

export const menuMeshComponentType = 'mp.menuMeshComponent';
export const makeMenuMeshComponent = (): any => new MenuMeshComponent();
